// Termos de uso, política de privacidade (LGPD) e as permissões do app.
// TextoLegal: o texto completo. Permissoes: as chaves de câmera, arquivos, localização e microfone.
// BoasVindas: primeiro acesso (ou termos novos): aceitar, autorizar e, em conta nova, usar o código de indicação.
import { useEffect, useState } from 'react';
import { cobrancaAtiva } from '../lib/plano';
import { Linking, Modal, Platform, ScrollView, Switch, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Camera } from 'expo-camera';
import type { Permissao } from '../store/types';
import { setDoc, useDoc } from '../store/store';
import { CONTATO_PRIVACIDADE, PRIVACIDADE, TERMOS, VERSAO_TERMOS, type Secao } from '../lib/termos';
import { contaNova } from '../lib/plano';
import { pegarIndicacao, usarCodigo } from '../lib/indicacao';
import { Btn, Field } from './ui';
import { C, alpha, useCores } from './theme';
import { N_, t } from '../lib/i18n';
import { ROLAGEM_TECLADO } from './teclado';

const PERMISSOES: { id: Permissao; icon: string; titulo: string; desc: string }[] = [
  { id: 'arquivos', icon: '📁', titulo: N_('Arquivos (importar e exportar)'), desc: N_('Anexar documentos, importar extratos e salvar relatórios, PDFs e cópias de segurança.') },
  { id: 'camera', icon: '📷', titulo: N_('Câmera e galeria'), desc: N_('Tirar fotos de boletos, cupons e documentos e escolher fotos e vídeos da galeria.') },
  { id: 'local', icon: '🌎', titulo: N_('Localização (país e fuso horário)'), desc: N_('Mostrar datas, horários, moeda e feriados certos para onde você está. A localização aproximada do celular só é usada quando você pede o clima do dia no Diário, e não fica guardada.') },
  { id: 'audio', icon: '🎙️', titulo: N_('Gravação de áudio'), desc: N_('Gravar lembretes e anotações por voz e ditar textos (🎤) quando você pedir. O microfone só liga com o seu toque, e o áudio do ditado não é guardado.') },
];
export const PERMISSOES_TODAS: Record<Permissao, boolean> = { arquivos: true, camera: true, local: true, audio: true };

function Bloco({ s }: { s: Secao }) {
  return (
    <View style={{ gap: 6 }}>
      <Text style={{ color: C.text, fontWeight: '700' }}>{t(s.titulo)}</Text>
      {s.textos.map((x, i) => <Text key={i} style={{ color: C.sub, fontSize: 13, lineHeight: 19 }}>{t(x)}</Text>)}
    </View>
  );
}

export function TextoLegal({ cor }: { cor: string }) {
  return (
    <View style={{ gap: 14 }}>
      <Text style={{ color: C.text, fontSize: 18, fontWeight: '800' }}>{t('Termos de uso')}</Text>
      <TextoTermos />
      <Text style={{ color: C.text, fontSize: 18, fontWeight: '800', marginTop: 8 }}>{t('Política de privacidade (LGPD)')}</Text>
      <TextoPrivacidade cor={cor} />
    </View>
  );
}
/** Só os termos de uso (sem o título), para Ajustes > Privacidade e termos > "Termos de uso" (0.67.2). */
export const TextoTermos = () => <View style={{ gap: 14 }}>{TERMOS.map(s => <Bloco key={s.titulo} s={s} />)}</View>;
/** Só a política de privacidade (sem o título), com o contato do encarregado e a versão (0.67.2). */
export function TextoPrivacidade({ cor }: { cor: string }) {
  return (
    <View style={{ gap: 14 }}>
      {PRIVACIDADE.map(s => <Bloco key={s.titulo} s={s} />)}
      <Text style={{ color: C.sub, fontSize: 13 }}>{t('Contato do encarregado de dados:')} <Text style={{ color: cor, fontWeight: '700' }}
        onPress={() => Linking.openURL(`mailto:${CONTATO_PRIVACIDADE}`).catch(() => {})}>{CONTATO_PRIVACIDADE}</Text></Text>
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Versão {v}', { v: VERSAO_TERMOS })}</Text>
    </View>
  );
}

/** Pede a permissão do sistema quando dá (câmera). As outras o sistema pede na hora do uso. */
async function pedirSistema(p: Permissao) {
  if (Platform.OS === 'web') return;
  try { if (p === 'camera') await Camera.requestCameraPermissionsAsync(); } catch { /* segue sem */ }
}

export function Permissoes({ cor, valor, onChange }: { cor: string; valor: Partial<Record<Permissao, boolean>>; onChange: (v: Partial<Record<Permissao, boolean>>) => void }) {
  return (
    <View style={{ gap: 4 }}>
      {PERMISSOES.map(p => (
        <View key={p.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 8 }}>
          <Text style={{ fontSize: 22, width: 28, textAlign: 'center' }}>{p.icon}</Text>
          <View style={{ flex: 1, gap: 2 }}>
            <Text style={{ color: C.text, fontWeight: '600' }}>{t(p.titulo)}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t(p.desc)}</Text>
          </View>
          <Switch value={valor[p.id] !== false} trackColor={{ true: cor }}
            onValueChange={v => { onChange({ ...valor, [p.id]: v }); if (v) pedirSistema(p.id); }} />
        </View>
      ))}
    </View>
  );
}

/** Primeiro acesso ou termos novos. Aparece por cima de tudo até a pessoa aceitar. */
export function BoasVindas() {
  const aj = useDoc('ajustes');
  const { accent: cor } = useCores();
  const ins = useSafeAreaInsets();
  const [pronto, setPronto] = useState(false);
  const [lerTudo, setLerTudo] = useState(false);
  const [perm, setPerm] = useState<Partial<Record<Permissao, boolean>>>(PERMISSOES_TODAS);
  const [codigo, setCodigo] = useState('');
  // veio pelo link do convite de um amigo: o código já aparece preenchido
  useEffect(() => { pegarIndicacao().then(c => { if (c) setCodigo(x => x || c); }); }, []);
  const [msg, setMsg] = useState<{ ok: boolean; msg: string } | null>(null);
  const [indo, setIndo] = useState(false);
  // espera a sincronização trazer o aceite feito em outro aparelho
  useEffect(() => { const x = setTimeout(() => setPronto(true), 2500); return () => clearTimeout(x); }, []);
  const precisa = pronto && aj.aceite?.versao !== VERSAO_TERMOS;
  if (!precisa) return null;
  const novo = contaNova() && !aj.indicadoPor && !aj.aceite;

  const aceitar = async () => {
    if (indo) return;
    setIndo(true);
    try {
      if (novo && codigo.trim()) {
        const r = await usarCodigo(codigo);
        setMsg(r);
        if (!r.ok) return;
      }
      for (const p of Object.keys(perm) as Permissao[]) if (perm[p]) await pedirSistema(p);
      setDoc('ajustes', d => ({ ...d, aceite: { versao: VERSAO_TERMOS, em: new Date().toISOString(), permissoes: perm } }));
    } finally { setIndo(false); }
  };

  return (
    <Modal visible animationType="fade" onRequestClose={() => {}} presentationStyle="fullScreen">
      <ScrollView {...ROLAGEM_TECLADO} style={{ flex: 1, backgroundColor: C.bg }} keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingTop: ins.top + 20, paddingBottom: ins.bottom + 40, paddingHorizontal: 20, gap: 14 }}>
        <Text style={{ fontSize: 44, textAlign: 'center' }}>{aj.aceite ? '📄' : '👋'}</Text>
        <Text style={{ color: C.text, fontSize: 22, fontWeight: '800', textAlign: 'center' }}>{aj.aceite ? t('Os termos foram atualizados') : t('Boas-vindas ao My Planner')}</Text>
        <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Antes de começar, confira o que o app usa e por quê. Você pode mudar tudo depois em Ajustes > Privacidade e termos.')}</Text>

        <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 6 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>{t('O que o app pode usar')}</Text>
          <Permissoes cor={cor} valor={perm} onChange={setPerm} />
        </View>

        {novo && cobrancaAtiva() && (
          <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 16, padding: 14, gap: 8 }}>
            <Text style={{ color: C.text, fontWeight: '700' }}>🎁 {t('Alguém indicou o app para você?')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Digite o código e ganhe 10 dias com todas as abas liberadas. Se não tiver, deixe em branco.')}</Text>
            <Field value={codigo} onChangeText={x => { setCodigo(x.toUpperCase()); setMsg(null); }} placeholder={t('Código de indicação (opcional)')} autoCapitalize="characters" autoCorrect={false} maxLength={10} />
            {!!msg && <Text style={{ color: msg.ok ? C.ok : C.danger, fontSize: 12 }}>{msg.msg}</Text>}
          </View>
        )}

        <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 8 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>{t('Termos de uso e privacidade')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Seus dados são seus: não vendemos e não usamos para publicidade. Você pode exportar ou apagar tudo quando quiser.')}</Text>
          <Text style={{ color: cor, fontWeight: '700' }} onPress={() => setLerTudo(x => !x)}>{lerTudo ? t('Esconder o texto completo') : t('Ler o texto completo')}</Text>
          {lerTudo && <TextoLegal cor={cor} />}
        </View>

        <Btn cor={cor} disabled={indo} label={indo ? t('Aguarde...') : t('Li e aceito os termos e a política de privacidade')} onPress={aceitar} />
      </ScrollView>
    </Modal>
  );
}
